import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, ShieldCheck, Users } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";

/**
 * Public landing page hit by anyone clicking a bulk workspace invite link.
 * Three states:
 *   1. Loading → fetching `/invite-links/:token/info`.
 *   2. Ready → render the join form (workspace name + role + domain hint).
 *   3. Success → "You're in" panel + auto-redirect into /projects.
 *
 * Backend keeps domain / expiry / rate-limit / generic-error logic; this
 * component is intentionally thin and never tries to second-guess the
 * server (rejected attempts surface as a single user-friendly error).
 */
export default function JoinWorkspacePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { refresh: refreshWorkspaces, switchTo } = useWorkspace();

  const [state, setState] = useState("loading"); // loading | ready | submitting | success | invalid
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get(`/invite-links/${token}/info`);
        setInfo(data);
        setState("ready");
      } catch (e) {
        setError(formatApiError(e));
        setState("invalid");
      }
    })();
  }, [token]);

  const allowedDomainHint = info?.allowed_domains?.length
    ? `Use an email at ${info.allowed_domains.map((d) => `@${d}`).join(" or ")}`
    : "Any email address works.";

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      setError("Email and password are required.");
      return;
    }
    setError("");
    setState("submitting");
    try {
      const { data } = await api.post(`/invite-links/${token}/claim`, {
        email: email.trim(),
        password,
        name: name.trim() || null,
      });
      // The backend already set auth cookies — refresh local user state +
      // workspace list, then point the workspace switcher at the new one.
      if (data.workspace_id) {
        localStorage.setItem("probe_workspace_id", data.workspace_id);
      }
      await refreshUser?.();
      await refreshWorkspaces?.();
      if (data.workspace_id) switchTo?.(data.workspace_id);
      setState("success");
      // Small delay so the user sees the success card before bouncing.
      setTimeout(() => navigate("/projects"), 900);
    } catch (err) {
      setError(formatApiError(err));
      setState("ready");
    }
  };

  // Already-signed-in convenience: prefill the email so a one-click join feels native.
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user?.email]);

  if (state === "loading") {
    return (
      <FullPageFrame>
        <div className="flex items-center gap-2 text-sm text-[#64748B] dark:text-white/60">
          <Loader2 size={14} className="animate-spin" /> Checking invitation…
        </div>
      </FullPageFrame>
    );
  }

  if (state === "invalid") {
    return (
      <FullPageFrame>
        <div className="text-center max-w-sm">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#FEF2F2] text-[#B91C1C] mb-3 dark:bg-[#7F1D1D]/30 dark:text-[#FCA5A5]">
            <AlertTriangle size={20} />
          </div>
          <h1 className="font-['Geist'] font-semibold text-xl text-[#0F172A] dark:text-white">
            This invitation can&apos;t be used
          </h1>
          <p className="text-sm text-[#64748B] dark:text-white/60 mt-1.5">{error || "The link may have expired, been revoked, or hit its seat cap."}</p>
          <Link
            to="/login"
            className="inline-block mt-5 text-sm font-medium text-[#3B82F6] hover:underline"
          >
            Go to sign in →
          </Link>
        </div>
      </FullPageFrame>
    );
  }

  if (state === "success") {
    return (
      <FullPageFrame>
        <div className="text-center max-w-sm">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#10B981]/15 text-[#047857] mb-3">
            <CheckCircle2 size={20} />
          </div>
          <h1 className="font-['Geist'] font-semibold text-xl text-[#0F172A] dark:text-white">
            You&apos;re in
          </h1>
          <p className="text-sm text-[#64748B] dark:text-white/60 mt-1.5">
            Taking you to your projects in <span className="font-medium text-[#0F172A] dark:text-white">{info.workspace_name}</span>…
          </p>
        </div>
      </FullPageFrame>
    );
  }

  return (
    <FullPageFrame>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-6 space-y-4 dark:bg-[#161618] dark:border-white/10"
        data-testid="join-workspace-form"
      >
        <div className="space-y-1.5">
          <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#3B82F6]/10 text-[#1D4ED8] text-[10px] font-semibold uppercase tracking-wide dark:bg-[#3B82F6]/15 dark:text-[#93C5FD]">
            <Users size={10} /> Workspace invite
          </div>
          <h1 className="font-['Geist'] font-semibold text-xl text-[#0F172A] tracking-tight dark:text-white">
            Join {info.workspace_name}
          </h1>
          <p className="text-sm text-[#64748B] dark:text-white/60">
            You&apos;ll join as <span className="font-medium text-[#0F172A] capitalize dark:text-white">{info.role}</span>. {allowedDomainHint}
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-[#475569] dark:text-white/60">Email</label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="join-email"
            className="rounded-md border-[#E5E7EB] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-[#475569] dark:text-white/60">
            Full name <span className="text-[#94A3B8] font-normal">(only needed for new accounts)</span>
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="join-name"
            className="rounded-md border-[#E5E7EB] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-[#475569] dark:text-white/60">Password</label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="At least 8 chars, with a number & symbol"
            data-testid="join-password"
            className="rounded-md border-[#E5E7EB] dark:bg-[#1F1F23] dark:border-white/10 dark:text-white"
          />
          <p className="text-[11px] text-[#94A3B8] dark:text-white/40">
            Existing user? Enter your current Probe password. New here? Pick a strong one.
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-[#FEF2F2] border border-[#FECACA] text-sm text-[#B91C1C] px-3 py-2 dark:bg-[#7F1D1D]/15 dark:border-[#7F1D1D]/40 dark:text-[#FCA5A5]" data-testid="join-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={state === "submitting"}
          data-testid="join-submit"
          className="w-full h-10 rounded-md bg-[#3B82F6] text-white text-sm font-medium hover:bg-[#2563EB] disabled:opacity-50 flex items-center justify-center gap-1.5"
        >
          {state === "submitting" && <Loader2 size={14} className="animate-spin" />}
          <ShieldCheck size={14} /> Join {info.workspace_name}
        </button>
      </form>
    </FullPageFrame>
  );
}

function FullPageFrame({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F8FAFC] dark:bg-[#0F1014]">
      {children}
    </div>
  );
}
